import { memo, useEffect, useState } from "react";
import SlideBackdrop from "../../../canvas/design/SlideBackdrop.jsx";
import { THEMES, ensureThemeFonts } from "../../../canvas/design/themes";
import { useStageScale } from "./studioShared";

/** Real sample slide rendered in the theme's own layout, fonts and colours. */
const ThemePreview = memo(function ThemePreview({ theme }) {
  const [ref, scale] = useStageScale();
  const { content } = theme.layouts;
  const c = theme.colors;
  const font = (role) => (role === "body" ? theme.fonts.body : theme.fonts.heading);
  return (
    <div className="cine-theme-preview" ref={ref}>
      <div className="cine-theme-preview-stage" style={{ transform: `scale(${scale})` }}>
        <SlideBackdrop background={theme.background} still />
        <div
          style={{
            position: "absolute",
            ...content.heading,
            fontFamily: font("heading"),
            fontSize: theme.styles.heading.size,
            fontWeight: theme.styles.heading.weight,
            color: c.heading,
            lineHeight: 1.05,
            letterSpacing: "-0.01em",
          }}
        >
          Cardiac Muscle Tissue
        </div>
        <div
          style={{
            position: "absolute",
            ...content.body,
            fontFamily: font("body"),
            fontSize: theme.styles.body.size + 4,
            color: c.text,
            lineHeight: 1.5,
          }}
        >
          <div style={{ color: c.accent, fontWeight: 700, fontSize: theme.styles.body.size, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 18 }}>
            Histology · Lecture 4
          </div>
          <div>• Striated, branching fibres</div>
          <div>• Central nuclei</div>
          <div>• Intercalated discs</div>
        </div>
        <div
          style={{
            position: "absolute",
            ...content.media,
            borderRadius: 18,
            overflow: "hidden",
            background:
              "radial-gradient(circle at 30% 35%, #f9a8d4 0 9%, transparent 10%), radial-gradient(circle at 62% 58%, #f9a8d4 0 8%, transparent 9%), radial-gradient(circle at 44% 78%, #f9a8d4 0 7%, transparent 8%), repeating-linear-gradient(115deg, #fbcfe8 0 22px, #f5b5d4 22px 30px)",
            boxShadow: `0 0 0 6px ${c.accent}33, 0 24px 60px rgba(0,0,0,0.25)`,
          }}
        />
      </div>
    </div>
  );
});

export default function ThemeTab({ activeThemeId, onApplyTheme, slideCount }) {
  const [restyleText, setRestyleText] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    THEMES.forEach(ensureThemeFonts);
  }, []);

  const apply = async (themeId, scope) => {
    setBusy(true);
    try {
      await onApplyTheme(themeId, scope, { restyleText });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="cine-apply-bar">
        <button
          type="button"
          className="cine-btn-primary cine-grow"
          disabled={!activeThemeId || busy}
          onClick={() => apply(activeThemeId, "all")}
        >
          {busy ? "Applying…" : `Apply to all ${slideCount} slides`}
        </button>
      </div>
      <p className="cine-studio-sub">
        Pick a theme to style this slide, then apply it to the whole deck. Your text, images,
        histology and 3D content stay exactly as they are.
      </p>
      <label className="cine-row" style={{ fontSize: 12.5, margin: "0 0 12px", cursor: "pointer" }}>
        <input type="checkbox" checked={restyleText} onChange={(e) => setRestyleText(e.target.checked)} />
        <span>Match text fonts and colours to the theme</span>
      </label>
      <div className="cine-theme-grid">
        {THEMES.map((theme) => {
          const active = theme.id === activeThemeId;
          return (
            <button
              key={theme.id}
              type="button"
              className={`cine-theme-card${active ? " is-active" : ""}`}
              onClick={() => apply(theme.id, "current")}
              aria-pressed={active}
              aria-label={`${theme.name} theme: ${theme.description}`}
              disabled={busy}
            >
              <ThemePreview theme={theme} />
              <div className="cine-theme-meta">
                <div style={{ minWidth: 0 }}>
                  <div className="cine-theme-name">{theme.name}</div>
                  <div className="cine-theme-desc">{theme.description}</div>
                </div>
                <div className="cine-theme-dots" aria-hidden="true">
                  <span style={{ background: theme.colors.heading }} />
                  <span style={{ background: theme.colors.accent }} />
                  <span style={{ background: theme.colors.accent2 }} />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}
